import React, { useState, useEffect } from 'react';
import { userService } from "../../../_services";
import { Navigate } from 'react-router-dom';
import moment from 'moment';
import UserDashboard from "../user/Dashboard";
import DateRangePicker from "../../ui/date-range-picker";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { ExternalLink, Search } from "lucide-react";

/**
 * User Transactions Page
 * Modernized with shadcn components - Claims dashboard for distributors
 */
function Transactions() {
    // Initialize dates
    const getInitialStartDate = () => {
        const stored = localStorage.getItem('User_Start_Date');
        if (stored && stored !== "" && stored !== null && stored !== undefined) {
            return stored;
        }
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    const getInitialEndDate = () => {
        const stored = localStorage.getItem('User_End_Date');
        if (stored && stored !== "" && stored !== null && stored !== undefined) {
            return stored;
        }
        return new Date().toISOString().split('T')[0];
    };

    // State
    const [dashboardListRendered, setDashboardListRendered] = useState(true);
    const [startDate, setStartDate] = useState(getInitialStartDate());
    const [endDate, setEndDate] = useState(getInitialEndDate());
    const [allDirectOverride, setAllDirectOverride] = useState('Direct');
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');
    const [dashboardName, setDashboardName] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [selectedTab, setSelectedTab] = useState('Total');

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [id, name] = Usermeta_Data.split(':');
        setUserId(id);
        setUserName(name);
        setDashboardName(name);

        // Initialize claim type
        window.localStorage.setItem('Updated_HD_Type', 'totalpaid');
    }, []);

    const handleChangeStartEnd = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            localStorage.setItem('User_Start_Date', value);
        } else if (type === 'end') {
            setEndDate(value);
            localStorage.setItem('User_End_Date', value);
        }
    };

    const handleDateRangeChange = (event, picker) => {
        const newStartDate = picker.startDate.format('YYYY-MM-DD');
        const newEndDate = picker.endDate.format('YYYY-MM-DD');
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        localStorage.setItem('User_Start_Date', newStartDate);
        localStorage.setItem('User_End_Date', newEndDate);
    };

    const handleChangeAllDirectOverride = (value) => {
        setAllDirectOverride(value);
    };

    const handleTabChange = (value) => {
        setSelectedTab(value);
        let claimType = 'totalpaid';
        if (value === 'Reversed') claimType = 'reversed';
        else if (value === 'Net') claimType = 'netpaid';
        else if (value === 'Paid') claimType = 'compensable';
        
        window.localStorage.setItem('Updated_HD_Type', claimType);
    };

    const handleChangeFilter = (e) => {
        if (e) {
            const cls_name = e.currentTarget?.className || '';
            const event_type = e.type;
            
            if (cls_name.includes('reset_filter_tag_button_event') && event_type === 'click') {
                const element = document.getElementById('totalpaid1');
                if (element) element.click();
            }
            
            if (localStorage.getItem('Updated_HD_Type') === 'totalpaid') {
                const element = document.getElementById('totalpaid1');
                if (element) element.click();
            }
        }
    };

    const viewOldDashboard = async () => {
        try {
            const apidata = await userService.claimdashboard(userId, userName);
            if (apidata && apidata.length > 0 && apidata[0].code !== "-1") {
                const currentUrl = window.location.href;
                const wparam = localStorage.getItem('wt19:awaydpset');
                
                const isDev = currentUrl.toLowerCase().includes("release-backoffice") || 
                             currentUrl.toLowerCase().includes("dev-backoffice") || 
                             currentUrl.toLowerCase().includes("localhost:");
                
                const url = isDev 
                    ? `http://dev.glichealth.com/login?q=${wparam}`
                    : `https://reporting.nulifespanrx.com/login?q=${wparam}`;
                
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error accessing old dashboard:', error);
        }
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate to="/signup" replace />;
    }

    localStorage.setItem('pagename', 'claimdashboard');

    return (
        <div className="w-full space-y-6">
            {/* Heading */}
            <h1 className="text-2xl font-semibold text-brand-dark">Dashboard</h1>
            
            {/* Date Pickers, Search, and Tabs Row */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                {/* Date Range Picker */}
                <DateRangePicker
                    startDate={moment(startDate)}
                    endDate={moment(endDate)}
                    onApply={handleDateRangeChange}
                    displayMode="unified"
                />
                
                {/* Search and Tabs */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                        <Input 
                            className="pl-10" 
                            placeholder="Search" 
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                        />
                    </div>
                    <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
                        <TabsList className="grid grid-cols-4 w-full">
                            <TabsTrigger value="Total">Total</TabsTrigger>
                            <TabsTrigger value="Reversed">Reversed</TabsTrigger>
                            <TabsTrigger value="Net">Net</TabsTrigger>
                            <TabsTrigger value="Paid">Paid</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </div>
            
            {/* Dashboard Content */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 px-6 py-3">
                    <div>
                        <CardTitle className="text-lg font-semibold text-brand-dark">Claims Dashboard</CardTitle>
                        <p className="text-sm text-brand-dark/70 mt-1">
                            Miss the old reporting dashboard? You can still access it using the button on the right.
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={viewOldDashboard}
                        className="text-brand-accent border-brand-accent hover:bg-brand-accent hover:text-white whitespace-nowrap"
                    >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Old Dashboard
                    </Button>
                </CardHeader>
                <CardContent className="pt-6">
                    {dashboardListRendered && (
                        <UserDashboard 
                            dist_usertype={allDirectOverride} 
                            dashboardName={dashboardName} 
                            handeChangefilter={handleChangeFilter} 
                            startDate={startDate} 
                            endDate={endDate} 
                        />
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default Transactions;
